import styled, { ThemeProvider, createGlobalStyle } from "styled-components";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { AnimatePresence } from "framer-motion";
import { useState, Suspense, lazy, useEffect } from "react";
import { ThemeContextProvider, useThemeContext } from "./context/ThemeContext";
import GlobalLoader from "./components/GlobalLoader";
import LazyLoader from "./components/LazyLoader";
import TopLinks from "./components/SchoolProjectsGallery/TopLinks";

const GlobalStyles = createGlobalStyle`
  body {
    background-color: ${({ theme }) => theme.bg};
    font-family: ${({ $font }) => $font};
    transition: background-color 0.3s ease;
  }

  *, *::before, *::after {
    font-family: inherit;
  }

  ::-webkit-scrollbar {
    width: 4px;
    height: 80px;
  }

  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.scrollbarTrack};
  }

  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.scrollbarThumb};
    border-radius: 6px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.scrollbarThumbHover};
  }

  * {
    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => theme.scrollbarThumb} ${({ theme }) => theme.scrollbarTrack};
  }
`;

// Lazy load components
const Navbar = lazy(() => import("./components/Navbar"));
const Hero = lazy(() => import("./components/sections/Hero"));
const Skills = lazy(() => import("./components/sections/Skills"));
const Experience = lazy(() => import("./components/sections/Experience"));
const Projects = lazy(() => import("./components/sections/Projects"));
const Education = lazy(() => import("./components/sections/Education"));
const Contact = lazy(() => import("./components/sections/Contact"));
const Footer = lazy(() => import("./components/sections/Footer"));
const ProjectDetails = lazy(() => import("./components/Dialog/ProjectDetails"));
const ProjectSlider = lazy(() => import("./components/sections/ProjectSlider"));
const ProjectDetailsPage = lazy(
  () => import("./components/pages/ProjectDetailsPage"),
);
const StarsCanvas = lazy(() => import("./components/canvas/Stars"));
const SchoolProjectsGallery = lazy(
  () => import("./components/SchoolProjectsGallery"),
);
const GitHubStats = lazy(() => import("./components/sections/GitHubStats"));
const Body = styled.div`
  background-color: ${({ theme }) => theme.bg};
  width: 100%;
  overflow-x: hidden;
  position: relative;
`;

const StarsBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  z-index: 0;
  opacity: 0.6;
  pointer-events: none;
`;

const Wrapper = styled.div`
  padding-bottom: 100px;
  background:
    linear-gradient(
      38.73deg,
      ${({ theme }) => theme.bgGradient1} 0%,
      transparent 50%
    ),
    linear-gradient(
      141.27deg,
      transparent 50%,
      ${({ theme }) => theme.bgGradient2} 100%
    );
  width: 100%;
  clip-path: polygon(0 0, 100% 0, 100% 100%, 30% 98%, 0 100%);
  position: relative;
  z-index: 1;
`;

// Home
const MainLayout = ({ openModal, setOpenModal }) => (
  <div style={{ position: "relative", zIndex: 1 }}>
    {/* Above-fold: Hero + Stats */}
    <Suspense fallback={<LazyLoader type="card" />}>
      <Hero />
      <GitHubStats />
      <ProjectSlider openModal={openModal} setOpenModal={setOpenModal} />
    </Suspense>

    <Wrapper>
      <TopLinks />
    </Wrapper>

    {/* Below-fold: Skills, Projects, Experience, Education, Contact */}
    <Suspense fallback={<LazyLoader type="card" />}>
      <Wrapper>
        <Skills />
        <Projects openModal={openModal} setOpenModal={setOpenModal} />
      </Wrapper>
      <Experience />
      <Wrapper>
        <Education />
        <Contact />
      </Wrapper>
      <Footer />
    </Suspense>

    {openModal.state && (
      <Suspense fallback={null}>
        <ProjectDetails openModal={openModal} setOpenModal={setOpenModal} />
      </Suspense>
    )}
  </div>
);

function AppContent() {
  const { currentTheme, currentFont } = useThemeContext();
  const [openModal, setOpenModal] = useState({ state: false, project: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const minDelay = 500;
    const start = Date.now();

    const done = () => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, minDelay - elapsed);
      setTimeout(() => setLoading(false), remaining);
    };

    if (document.readyState === "complete") {
      done();
    } else {
      window.addEventListener("load", done, { once: true });
      // Fallback max wait of 2s
      const fallback = setTimeout(done, 2000);
      return () => {
        window.removeEventListener("load", done);
        clearTimeout(fallback);
      };
    }
  }, []);

  if (loading) {
    return <GlobalLoader />;
  }

  // Routes
  return (
    <ThemeProvider theme={currentTheme}>
      <GlobalStyles $font={currentFont} />
      <Router>
        <Suspense fallback={null}>
          <Navbar />
        </Suspense>

        <Body>
          <StarsBackground>
            <Suspense fallback={null}>
              <StarsCanvas />
            </Suspense>
          </StarsBackground>

          <AnimatePresence>
            <Routes>
              <Route
                path="/"
                element={
                  <MainLayout
                    openModal={openModal}
                    setOpenModal={setOpenModal}
                  />
                }
              />
              <Route
                path="/project/:slug"
                element={
                  <Suspense fallback={<LazyLoader type="card" />}>
                    <ProjectDetailsPage />
                  </Suspense>
                }
              />
              <Route
                path="/school-projects-gallery"
                element={
                  <Suspense fallback={<LazyLoader type="card" />}>
                    <SchoolProjectsGallery />
                  </Suspense>
                }
              />
            </Routes>
          </AnimatePresence>
        </Body>
      </Router>
    </ThemeProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeContextProvider>
        <AppContent />
      </ThemeContextProvider>
    </HelmetProvider>
  );
}

export default App;
